import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, ArrowRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { logger } from "../lib/logger";

const QuotePaymentSuccessPage = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [quote, setQuote] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);

  const fetchQuote = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      setQuote(response.data);
    } catch (error) {
      toast.error("Failed to load quote");
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPolling(false);
      setLoading(false);
      toast.error("Payment verification timed out. Please check your email for confirmation.");
      fetchQuote();
      return;
    }

    try {
      const response = await axios.get(
        `${API}/quotes/${quoteId}/payment/status?session_id=${sessionId}`
      );
      setPaymentStatus(response.data);

      if (response.data.payment_status === "paid") {
        setPolling(false);
        toast.success("Payment successful!");
        fetchQuote();
      } else if (response.data.status === "expired") {
        setPolling(false);
        setLoading(false);
        toast.error("Payment session expired");
        fetchQuote();
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      logger.error("Payment status check failed:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  }, [quoteId, sessionId, fetchQuote]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setPolling(false);
      fetchQuote();
    }
  }, [sessionId, pollPaymentStatus, fetchQuote]);

  const handleDownloadPDF = () => {
    window.open(`${API}/quotes/${quoteId}/pdf`, "_blank");
  };

  if (loading || polling) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center" data-testid="payment-processing">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-16 h-16 text-[#00341d] mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl font-bold text-[#00341d] uppercase mb-2">
              Processing Payment
            </h2>
            <p className="text-zinc-600">
              Please wait while we verify your payment...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPaid = quote?.payment_status === "paid" || paymentStatus?.payment_status === "paid";

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="payment-success-page">
      {/* Header */}
      <header className="bg-[#00341d] text-white py-6">
        <div className="max-w-3xl mx-auto px-6">
          <img
            src="https://customer-assets.emergentagent.com/job_665c49ba-1471-4262-8719-d672e8150062/artifacts/l1uvt1c1_Logo_Framed_Green.png"
            alt="Land ClearCo"
            className="h-12 mx-auto"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <Card className="bg-white shadow-2xl rounded-none overflow-hidden border-t-8 border-[#00341d]">
          <CardContent className="p-8 md:p-12">
            {isPaid ? (
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#00341d] uppercase mb-4">
                  Payment Successful!
                </h1>
                
                <p className="text-lg text-zinc-600 mb-8 max-w-md mx-auto">
                  Thank you for your payment. We appreciate your business and look forward to serving you again.
                </p>

                {quote && (
                  <div className="bg-zinc-50 rounded-lg p-6 mb-8 text-left max-w-sm mx-auto">
                    <h3 className="font-bold text-[#00341d] mb-3">Payment Details</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Quote Number</span>
                        <span className="font-medium">{quote.quote_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Amount Paid</span>
                        <span className="font-bold text-green-600">
                          ${(quote.total <= 3000 ? quote.total : quote.deposit_amount)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      {quote.total > 3000 && (
                        <div className="flex justify-between pt-2 border-t border-zinc-200">
                          <span className="text-zinc-500">Balance Due</span>
                          <span className="font-medium">
                            ${(quote.total - quote.deposit_amount)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Google Review Request */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-2 text-center max-w-sm mx-auto">
                    <div className="flex justify-center gap-0.5 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <svg key={`star-${i}`} className="w-6 h-6 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-sm text-amber-900 leading-relaxed mb-4">
                      Reviews and word of mouth is everything to a local business like ours. If you're satisfied with our work, would you be against giving us 5 stars on Google?
                    </p>
                    <a
                      href="https://g.page/r/CeseVks17yaaEBI/review"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#00341d] hover:bg-[#002816] text-white text-sm font-bold uppercase tracking-wide px-5 py-2.5 rounded transition-colors"
                      data-testid="google-review-link"
                    >
                      Leave a Google Review
                    </a>
                  </div>

                  <Button
                    onClick={handleDownloadPDF}
                    className="w-full md:w-auto bg-[#00341d] hover:bg-[#002816] text-white font-bold uppercase tracking-wide px-8 h-12"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Receipt
                  </Button>
                  
                  <div className="pt-4">
                    <Link
                      to={`/quote/${quoteId}`}
                      className="text-[#00341d] hover:text-[#002816] font-medium inline-flex items-center gap-2"
                    >
                      View Quote Details
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-amber-800 uppercase mb-4">
                  Payment Pending
                </h1>
                
                <p className="text-lg text-zinc-600 mb-8">
                  We couldn't verify your payment status. If you completed the payment, please check your email for confirmation.
                </p>

                <Link
                  to={`/quote/${quoteId}`}
                  className="inline-flex items-center gap-2 bg-[#00341d] hover:bg-[#002816] text-white font-bold uppercase tracking-wide px-8 h-12 rounded-sm"
                >
                  Return to Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-zinc-500 text-sm">
          <p>Land ClearCo, LLC &copy; {new Date().getFullYear()}</p>
          <p className="mt-1">Professional Land Clearing Services</p>
        </div>
      </main>
    </div>
  );
};

export default QuotePaymentSuccessPage;
